import { useAuth } from 'app/providers/auth/AuthProvider'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  getScheduleDoctorRailSelection,
  setScheduleDoctorRailSelection,
} from './scheduleDoctorRailSelectionStorage'

export const useScheduleDoctorRailSelection = (railStaff: { id: number }[]) => {
  const { userId } = useAuth()
  const [savedStaffIds, setSavedStaffIds] = useState<number[]>(() =>
    getScheduleDoctorRailSelection(userId),
  )
  const savedStaffIdsRef = useRef(savedStaffIds)
  const railIdsKey = railStaff.map((member) => member.id).join(',')

  useEffect(() => {
    const stored = getScheduleDoctorRailSelection(userId)
    savedStaffIdsRef.current = stored
    setSavedStaffIds(stored)
  }, [userId])

  // Saved doctors missing from today's rail stay saved; they only drop out of view.
  const focusedStaffIds = useMemo(() => {
    if (!railIdsKey) return []
    const allowed = new Set(railIdsKey.split(',').map(Number))
    return savedStaffIds.filter((staffId) => allowed.has(staffId))
  }, [railIdsKey, savedStaffIds])

  const persist = useCallback(
    (nextIds: number[]) => {
      const next = setScheduleDoctorRailSelection(userId, nextIds)
      savedStaffIdsRef.current = next
      setSavedStaffIds(next)
    },
    [userId],
  )

  const toggleFocusedStaff = useCallback(
    (staffId: number) => {
      const current = savedStaffIdsRef.current
      const next = current.includes(staffId)
        ? current.filter((id) => id !== staffId)
        : [...current, staffId]
      persist(next)
    },
    [persist],
  )

  const clearFocusedStaff = useCallback(() => {
    persist([])
  }, [persist])

  return { focusedStaffIds, toggleFocusedStaff, clearFocusedStaff }
}
